import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import {Context} from '../context/AppContext';

const EditCartModal = ({ item, modal, setModal }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [special_instructions, setSpecialInstructions] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [alertMessages, setAlertMessages] = useState({});
  const [disabledAddToCart, setDisabledAddToCart] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { changedCart, setChangedCart, restaurantId } = useContext(Context);

  useEffect(() => {
    if (modal && item) {
      // Prefill selected options if the item already has some selected
      if (item?.options) {
        const optionsToAdd = item.options.flatMap((attribute) =>
          ({
            id: attribute.id,
            priceModifier: attribute.priceModifier,
            attributeId: attribute.attributeId,
          })
        );
        setSelectedOptions(optionsToAdd);
      }

      // Prefill quantity if the item already has a quantity set
      if (item.quantity) {
        setQuantity(item.quantity);
      }

      if (item.special_instructions) {
        setSpecialInstructions(item.special_instructions);
      } else {
        setSpecialInstructions('');
      }

    }
  }, [modal, item]);

  useEffect(() => {
    const validateOptions = () => {
      let allOptionsValid = true;
      let alerts = {};
  
      item?.Restaurant_Item_Attributes.forEach((attribute) => {
        const requiredOptions = attribute?.Item_Attribute?.requ || 0;
        const maxOptions = attribute?.Item_Attribute?.max_ || 0;
        const selectedOptionsCount = selectedOptions.filter(opt => opt.attributeId === attribute.id).length;
  
        // Check for required options
        if (requiredOptions > selectedOptionsCount && requiredOptions !== 0) {
          allOptionsValid = false;
          alerts[attribute.id] = `*Must Select ${requiredOptions} options and you selected ${selectedOptionsCount}`;
        }
  
        // Check for maximum options
        if (maxOptions < selectedOptionsCount && maxOptions !== 0) {
          allOptionsValid = false;
          alerts[attribute.id] = `*Maximum of ${maxOptions} options can be selected and you selected ${selectedOptionsCount}`;
        }
  
        // Clear the alert if no issue
        if (!alerts[attribute.id]) {
          alerts[attribute.id] = '';
        }
      });
  
      setAlertMessages(alerts);
      setDisabledAddToCart(!allOptionsValid);
    };
  
    validateOptions();
  }, [selectedOptions, item]);

  useEffect(() => {
    if (!modal) {
      // Reset state when modal is closed
      setSelectedOptions([]);
      setSpecialInstructions('');
      setQuantity(1);
      setAlertMessages({});
      setDisabledAddToCart(false);
    }
  }, [modal]);

  const handleIncrease = () => {
    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const handleDecrease = () => {
    if (quantity > 1) {
      setQuantity((prevQuantity) => prevQuantity - 1);
    }
  };

  const handleChange = (e) => {
    const newQuantity = parseInt(e.target.value);
    if (!isNaN(newQuantity)) {
      setQuantity(newQuantity);
    }
  };

  const handleOptionChange = (optionId, priceModifier, attributeId) => {
    const existingOption = selectedOptions.find((opt) => opt.id === optionId);

    if (existingOption) {
      // If the option is already selected, remove it
      setSelectedOptions((prevOptions) =>
        prevOptions.filter((opt) => opt.id !== optionId)
      );
    } else {
      // If the option is not selected, add it
      setSelectedOptions((prevOptions) => [
        ...prevOptions,
        { id: optionId, priceModifier, attributeId },
      ]);
    }
  };

  // Calculate the total price based on selected options
  const calculateTotalPrice = () => {
    let totalPrice = +item?.price || 0;
    selectedOptions.forEach((option) => {
      totalPrice += +option.priceModifier || 0;
    });

    totalPrice *= +quantity;
    return totalPrice?.toFixed(2);
  };

  // Handle add item to cart
  const handleUpdateItem = () => {
    setIsLoading(true);

    const updatedItem = {
      Item: item,
      quantity,
      special_instructions,
      options: selectedOptions,
      price: calculateTotalPrice(),
    };
    const localCart = JSON.parse(localStorage.getItem(`cart${restaurantId}`));
    const updatedCart = localCart.map((cartItem) => {
      if (cartItem.Item.id === item.id) {
        return updatedItem;
      }
      return cartItem;
    });

    localStorage.setItem(`cart${restaurantId}`, JSON.stringify(updatedCart));
    setChangedCart(!changedCart);
    setIsLoading(false);
    setModal(false);
  }

  // Handle remove item from cart
  const handleRemoveItem = () => {
    setIsDeleting(true);
    const localCart = JSON.parse(localStorage.getItem(`cart${restaurantId}`));
    const updatedCart = localCart.filter(cartItem => cartItem.Item.id !== item.id);
    localStorage.setItem(`cart${restaurantId}`, JSON.stringify(updatedCart));
    setIsDeleting(false);
    setModal(false);
    setChangedCart(!changedCart);
  };

  return (
    <>   
     <Modal show={modal} onHide={() => setModal(false)} centered scrollable size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{item?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center">
          {item?.image_url && (
            <img
              src={item?.image_url}
              alt={item?.name}
              className="img-fluid mb-3"
              style={{ width: '100%', maxHeight: '220px', objectFit: 'cover' }}
            />
          )}
          <p>{item?.description?.length > 1 ? item?.description : item?.name}</p>
          {/* Attributes Section */}
          {item?.Restaurant_Item_Attributes && item?.Restaurant_Item_Attributes?.length > 0 && (
            <div className="w-100">
              <h6>Options:</h6>
              <Form>
              {item?.Restaurant_Item_Attributes
    ?.sort((a, b) => {
      // Sorting by name numerically or alphabetically
      return a?.Item_Attribute?.name?.localeCompare(b?.Item_Attribute?.name, undefined, { numeric: true });
    })?.map((attribute) => (
                  <Form.Group key={attribute.id} className="mb-3">
                    <Form.Label className="mb-2">{attribute?.Item_Attribute?.name} <br />
                      <small className="text-danger">{alertMessages[attribute.id]}</small>
                    </Form.Label>

                    <div className="d-flex flex-wrap">
                      {attribute?.Item_Attribute?.Item_Attribute_Options?.map((option) => (
                        <div className='checkbox-wrapper-47 m-0' key={option.id}>
                          <input
                            type="checkbox"
                            id={option.name}
                            name={option.name}
                            value={option.id}
                            onChange={() => handleOptionChange(option.id, option.price_modifier, attribute.id)}
                            checked={selectedOptions.some((opt) => opt.id === option.id)}
                          />
                          <label htmlFor={option.name}>{option.name} {option?.price_modifier > 0 ? `+ $${option.price_modifier}` : null}</label>
                        </div>
                      ))}
                    </div>
                  </Form.Group>
                ))}
              </Form>
            </div>
          )}
          {/* Special Instructions */}
   {/* ─────────── SPECIAL INSTRUCTIONS (clean Bootstrap styling) ─────────── */}
<Form.Group className="w-100 mb-4 px-2">
  <Form.Label className="fw-semibold mt-2 text-black d-block mb-2">
    Special Instructions
  </Form.Label>

  <div
    className="p-2 bg-white border"
    style={{
      borderRadius: 14,
      borderColor: "rgba(0,0,0,0.08)",
    }}
  >
    <Form.Control
      as="textarea"
      rows={2}
      placeholder="Add any special requests or dietary preferences…"
      value={special_instructions}
      onChange={(e) => setSpecialInstructions(e.target.value)}
      className="border-0 shadow-none"
      style={{
        resize: "vertical",
        minHeight: 70,
        fontSize: "0.95rem",
        lineHeight: 1.35,
        padding: "10px 12px",
        background: "transparent",
      }}
    />
  </div>

  <small className="text-muted d-block mt-1">
    Clear, concise notes help the kitchen get it right.
  </small>
</Form.Group>

          {/* Quantity choose */}
        </div>
      </Modal.Body>
  <div
  className="row align-items-center g-2 py-2 px-3 border-top bg-white"
  style={{
    position: "sticky",
    bottom: 0,
    zIndex: 10,
  }}
>
  {/* Quantity Selector */}
  <div className="col-12 col-md-4">
    <div className="d-flex justify-content-center align-items-center bg-light rounded-pill p-2 border">
      <button
        type="button"
        className="btn btn-light border-0 d-flex align-items-center justify-content-center"
        onClick={handleDecrease}
        disabled={quantity <= 1}
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
        }}
      >
        <FontAwesomeIcon icon={faMinus} style={{ color: "#9d2a00" }} />
      </button>

      <span
        className="fw-bold mx-3"
        style={{ fontSize: "1rem", color: "#212529" }}
      >
        {quantity}
      </span>

      <button
        type="button"
        className="btn btn-light border-0 d-flex align-items-center justify-content-center"
        onClick={handleIncrease}
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
        }}
      >
        <FontAwesomeIcon icon={faPlus} style={{ color: "#9d2a00" }} />
      </button>
    </div>
  </div>

  {/* Remove Button */}
  <div className="col-12 col-md-4">
    <button
      type="button"
      className="btn w-100 fw-semibold py-3 border-0"
      onClick={handleRemoveItem}
      disabled={isDeleting}
      style={{
        backgroundColor: "#dc3545",
        color: "#fff",
        borderRadius: "30px",
        fontSize: "1rem",
        opacity: isDeleting ? 0.7 : 1,
        transition: "background-color 0.2s ease",
      }}
      onMouseEnter={(e) => (e.target.style.backgroundColor = "#bb2d3b")}
      onMouseLeave={(e) => (e.target.style.backgroundColor = "#dc3545")}
    >
      {isDeleting ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
          ></span>
          Removing…
        </>
      ) : (
        "Remove"
      )}
    </button>
  </div>

  {/* Update / Add to Order */}
  <div className="col-12 col-md-4">
    <button
      type="button"
      className="btn w-100 fw-semibold py-3 border-0"
      style={{
        backgroundColor: "#9d2a00",
        color: "#fff",
        borderRadius: "30px",
        fontSize: "1rem",
        opacity: disabledAddToCart ? 0.6 : 1,
        cursor: disabledAddToCart ? "not-allowed" : "pointer",
        transition: "background-color 0.2s ease",
      }}
      disabled={disabledAddToCart}
      onClick={!disabledAddToCart ? handleUpdateItem : undefined}
      onMouseEnter={(e) => (e.target.style.backgroundColor = "#b83500")}
      onMouseLeave={(e) => (e.target.style.backgroundColor = "#9d2a00")}
    >
      {isLoading ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
          ></span>
          Updating…
        </>
      ) : (
        `Update Order • $${calculateTotalPrice()}`
      )}
    </button>
  </div>
</div>

    </Modal>
    </>
  );


}

export default EditCartModal;
